import React from "react";
import { useTranslation } from "react-i18next";
import type { RecentItem } from "../../hooks/useRecentConversions";
import { openMarkdownInReview } from "../../pages/ReviewPage";

interface Props {
  items: RecentItem[];
  onRestore: (item: RecentItem) => void;
  onRemove: (timestamp: number) => void;
}

function formatTime(ts: number): string {
  const diffMin = Math.floor((Date.now() - ts) / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  return new Date(ts).toLocaleDateString();
}

export const RecentConversions: React.FC<Props> = ({ items, onRestore, onRemove }) => {
  const { t } = useTranslation();
  if (!items.length) return null;

  return (
    <div className="recent-list">
      <h3 className="recent-list__title">{t("recent.title")}</h3>
      <ul className="recent-list__items">
        {items.map((item) => (
          <li key={item.timestamp} className="recent-item">
            <button
              className="recent-item__body"
              onClick={() => onRestore(item)}
              disabled={!item.restorable}
              title={item.restorable ? undefined : t("recent.tooLarge")}
            >
              <span className="recent-item__name">{item.filename}</span>
              <span className="recent-item__meta">
                {item.pageCount}p · {item.wordCount.toLocaleString()} {t("recent.words")} · {formatTime(item.timestamp)}
                {!item.restorable && ` · ${t("recent.tooLarge")}`}
              </span>
            </button>
            <div className="recent-item__actions">
              {item.restorable && (
                <button
                  className="btn btn--ghost btn--sm"
                  title={t("review.title")}
                  onClick={() => openMarkdownInReview(item.markdown)}
                >
                  ↗
                </button>
              )}
              <button
                className="btn btn--ghost btn--sm"
                title={t("recent.remove")}
                onClick={() => onRemove(item.timestamp)}
              >
                ✕
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
